/**
 * KarroFin Admin — mock query layer.
 *
 * Each function here answers the same question a real endpoint would, takes the
 * same params the real endpoint takes (page_number, page_length, search_query,
 * from_date, to_date, product_type), and returns the same envelope shape.
 * That is what makes the eventual swap a one-line change in api/index.js.
 *
 * Filtering and paging are done here rather than in the components on purpose:
 * the real endpoints page server-side, so the components must never assume they
 * hold the full dataset.
 */
import D, {
  enrichedUsers,
  filterOptions,
  docStatusFor,
  userById,
  products,
  offers,
  loans,
  notificationHistory,
  utmOptions,
  offersFor,
  loanFor,
} from "./dataset";
import {
  PRODUCT_FILTER_ALL,
  PRODUCT_TYPES,
  OFFER_LOAN_STATUS,
  PRECHECK_STATUS,
  LEAD_BUCKET,
  LEAD_BUCKETS,
  APPLICATION_STAGE,
  APPLICATION_STAGES,
  USER_STAGE,
  USER_STAGES,
  EC1_PROFILE_STATUSES,
  PROFILE_STATUS,
  CHANNEL,
  DELIVERY_STATUS,
  STATUS_COLOR,
  DEFAULT_STATUS_COLOR,
} from "../constants";
import { PAGE_LENGTH } from "../config";

/* ---------------------------------------------------------------- utils */

export function inRange(iso, from, to) {
  if (!from || !to) return true;
  const d = String(iso).slice(0, 10);
  return d >= from && d <= to;
}

function matchesProduct(productType, filter) {
  if (!filter || filter === PRODUCT_FILTER_ALL) return true;
  return productType === filter;
}

/**
 * Whether a USER falls inside a product-type filter.
 *
 * Not a plain comparison against CustomUser.customer_type: that field records
 * what the user came in asking for, while the offers they actually hold can
 * span products. Comparing the declared type alone makes a funnel that starts
 * narrow and then widens, because the offer-side counts do not agree with it.
 *
 * The rule that actually holds: a user belongs to a product if they hold an
 * offer of that product. Users who have no offers yet (EC1 / EC-rejected)
 * fall back to their declared customer_type so they still appear under PL/BL.
 */
function userInProductScope(user, filter) {
  if (!filter || filter === PRODUCT_FILTER_ALL) return true;
  const mine = offersFor(user.id);
  if (mine.length) return mine.some((o) => o.product_type === filter);
  return user.customer_type === filter;
}

export function paginate(rows, params) {
  const page = Math.max(1, Number(params.page_number) || 1);
  const size = Number(params.page_length) || PAGE_LENGTH;
  const total = rows.length;
  const total_page_count = Math.max(1, Math.ceil(total / size));
  const start = (page - 1) * size;
  return {
    results: rows.slice(start, start + size),
    total_count: total,
    total_page_count,
    page_number: page,
  };
}

/**
 * Does `value` match `wanted`?
 *
 * `wanted` is a single value from a filter control, or an ARRAY when the filter
 * was locked for this user by an admin (Team -> Filtered view). Falsy means
 * "not filtering".
 */
export function matchesAny(value, wanted) {
  if (wanted === null || wanted === undefined || wanted === "") return true;
  if (Array.isArray(wanted)) {
    return wanted.length === 0 || wanted.indexOf(value) !== -1;
  }
  return value === wanted;
}

export function textMatch(haystackParts, needle) {
  if (!needle) return true;
  const q = String(needle).toLowerCase().trim();
  return haystackParts.filter(Boolean).some((p) => String(p).toLowerCase().includes(q));
}

function colorFor(status) {
  return STATUS_COLOR[status] || DEFAULT_STATUS_COLOR;
}


/* ------------------------------------------------------------- dashboard */

export function dashboard(params = {}) {
  // A locked product_type arrives as an array; the scoping helpers below take a
  // single value, so the first locked one is used.
  const pt = Array.isArray(params.product_type)
    ? params.product_type[0] || null
    : params.product_type;
  const [from, to] = params.date_range || [];

  const scopedUsers = enrichedUsers.filter(
    (u) => inRange(u.created, from, to) && userInProductScope(u, pt)
  );
  const scopedOffers = offers.filter(
    (o) => matchesProduct(o.product_type, pt) && inRange(o.created, from, to)
  );

  // Tab counts: leads in the date range, per product. The same measure the
  // Leads screen uses, so the two agree for the same range, and the same one
  // the first KPI tile and the top of the funnel report. "All" leads so the
  // dashboard can hand this straight to the switch.
  const inWindow = enrichedUsers.filter((u) => inRange(u.created, from, to));
  const product_counts = [
    { label: PRODUCT_FILTER_ALL, count: inWindow.length },
  ].concat(
    PRODUCT_TYPES.map((label) => ({
      label,
      count: inWindow.filter((u) => userInProductScope(u, label)).length,
    }))
  );

  const clicked = scopedOffers.filter((o) => o.flag_offer_availed);
  const disbursedOffers = scopedOffers.filter((o) => o.flag_disbursed);
  const rejectedOffers = scopedOffers.filter(
    (o) => o.loan_status === OFFER_LOAN_STATUS.REJECTED
  );
  const ec1 = scopedUsers.filter(
    (u) => EC1_PROFILE_STATUSES.indexOf(u.profile_status) !== -1
  );
  const underProcess = scopedOffers.filter(
    (o) => o.loan_sub_status === PRECHECK_STATUS.UNDER_PROCESS
  );

  const disbursedAmount = disbursedOffers.reduce(
    (s, o) => s + (o.disbursal_amount || 0),
    0
  );

  // -- conversion ratios --------------------------------------------------
  //
  // A lead IS a clicked offer. That is not a simplification for the mock: the
  // Leads table server-side (leads/models.py) holds one FK to Offers and
  // nothing else, and its rows are written by a single post_save receiver
  // whose only condition is `if instance.flag_offer_availed`. So a lead is one
  // clicked offer -- not one customer, and not one loan.
  //
  // Subsetted from `clicked` rather than reusing disbursedOffers /
  // rejectedOffers above. An offer cannot be disbursed without being clicked,
  // but nothing in the data model enforces that, and a 118% conversion rate on
  // a dashboard is worse than no conversion rate at all.
  const disbursedLeads = clicked.filter((o) => o.flag_disbursed);
  const rejectedLeads = clicked.filter(
    (o) => o.loan_status === OFFER_LOAN_STATUS.REJECTED
  );

  // The bridge ratio, and deliberately the SAME entry in both groups: it is
  // what carries offers into the lead funnel, so it belongs to each and
  // changes with neither. Flipping the toggle swaps the other two only.
  const offerToLead = {
    key: "offer_lead",
    label: "Offer → Lead",
    part: clicked.length,
    whole: scopedOffers.length,
    whole_label: "offers",
    icon: "mouse-pointer-click",
    color: "#00897B",
  };

  // part/whole rather than a finished percentage, so the tile can print
  // "1,234 of 5,678 offers" underneath and the zero-denominator case stays one
  // decision in one place. whole_label travels with the row because this is
  // what knows which population it counted -- deriving that noun on the screen
  // means a conditional that has to know offer_lead is the odd one out below.
  //
  // Icons and colours mirror the KPI tiles they correspond to, so Disbursed is
  // the same green in both rows.
  const ratios = {
    offer: [
      offerToLead,
      {
        key: "offer_disbursed",
        label: "Offer → Disbursed",
        part: disbursedOffers.length,
        whole: scopedOffers.length,
        whole_label: "offers",
        icon: "circle-check",
        color: "#2E7D32",
      },
      {
        key: "offer_rejected",
        label: "Offer → Rejected",
        part: rejectedOffers.length,
        whole: scopedOffers.length,
        whole_label: "offers",
        icon: "circle-alert",
        color: "#C62828",
      },
    ],
    lead: [
      offerToLead,
      {
        key: "lead_disbursed",
        label: "Lead → Disbursed",
        part: disbursedLeads.length,
        whole: clicked.length,
        whole_label: "leads",
        icon: "circle-check",
        color: "#2E7D32",
      },
      {
        key: "lead_rejected",
        label: "Lead → Rejected",
        part: rejectedLeads.length,
        whole: clicked.length,
        whole_label: "leads",
        icon: "circle-alert",
        color: "#C62828",
      },
    ],
  };

  const kpis = [
    { key: "leads", label: "Total Leads", value: scopedUsers.length, icon: "users", color: "#0288D1" },
    { key: "offers", label: "Offers Generated", value: scopedOffers.length, icon: "tag", color: "#1976D2" },
    { key: "clicks", label: "Offers Clicked", value: clicked.length, icon: "mouse-pointer-click", color: "#00897B", sub: rateLabel(clicked.length, scopedOffers.length) },
    { key: "ec1", label: "Sign up", value: ec1.length, icon: "clipboard-list", color: "#7B1FA2" },
    { key: "under_process", label: "Under Process", value: underProcess.length, icon: "clock", color: "#F57C00" },
    { key: "disbursed", label: "Disbursed", value: disbursedOffers.length, icon: "circle-check", color: "#2E7D32" },
    { key: "rejected", label: "Rejected", value: rejectedOffers.length, icon: "circle-alert", color: "#C62828" },
    { key: "amount", label: "Disbursed Amount", value: disbursedAmount, icon: "banknote", color: "#004D40", is_currency: true },
  ];

  // Funnel semantics: each stage counts distinct users who reached AT LEAST
  // that stage, so the steps narrow strictly.
  //
  // "In process" must therefore include users who have already been disbursed
  // -- they passed through processing to get there. Counting only the users
  // currently sitting at Under Process would make the funnel widen at the
  // final step, which is exactly the sort of number that destroys trust in a
  // dashboard.
  const usersWithOffers = new Set(scopedOffers.map((o) => o.user_id));
  const usersClicked = new Set(clicked.map((o) => o.user_id));
  const usersDisbursed = new Set(disbursedOffers.map((o) => o.user_id));
  const usersInProcess = new Set(underProcess.map((o) => o.user_id));
  usersDisbursed.forEach((id) => usersInProcess.add(id));

  // Labels say "users" explicitly. The KPI row above counts OFFERS, so reusing
  // "Offers generated" here would put 138 next to 462 under the same words and
  // read as a contradiction rather than as two different measures.
  const funnel = [
    { label: "Leads", value: scopedUsers.length, color: "#0288D1" },
    { label: "Users with an offer", value: usersWithOffers.size, color: "#1976D2" },
    { label: "Users who clicked", value: usersClicked.size, color: "#00897B" },
    { label: "Users in process or beyond", value: usersInProcess.size, color: "#F57C00" },
    { label: "Users disbursed", value: usersDisbursed.size, color: "#2E7D32" },
  ];

  // Product-type split
  const byProduct = {};
  scopedOffers.forEach((o) => {
    byProduct[o.product_type] = (byProduct[o.product_type] || 0) + 1;
  });
  const productSplit = Object.keys(byProduct).map((k) => ({
    label: k,
    value: byProduct[k],
  }));

  // Disbursals over the last 12 weeks (spline)
  const weeks = 12;
  const series = [];
  const categories = [];
  for (let w = weeks - 1; w >= 0; w--) {
    const end = new Date(D.TODAY.getTime());
    end.setDate(end.getDate() - w * 7);
    const start = new Date(end.getTime());
    start.setDate(start.getDate() - 6);
    const s = start.toISOString().slice(0, 10);
    const e = end.toISOString().slice(0, 10);
    const count = disbursedOffers.filter(
      (o) => o.disbursal_date >= s && o.disbursal_date <= e
    ).length;
    const clicks = clicked.filter(
      (o) => o.clicked_at && o.clicked_at.slice(0, 10) >= s && o.clicked_at.slice(0, 10) <= e
    ).length;
    categories.push(`${String(end.getDate()).padStart(2, "0")}/${String(end.getMonth() + 1).padStart(2, "0")}`);
    series.push({ clicks, disbursed: count });
  }

  // Recent activity — most recent clicks and disbursals interleaved
  const recent = scopedOffers
    .filter((o) => o.flag_offer_availed)
    .sort((a, b) => (a.clicked_at < b.clicked_at ? 1 : -1))
    .slice(0, 8)
    .map((o) => {
      const u = enrichedUsers.find((x) => x.id === o.user_id) || {};
      return {
        id: o.id,
        user_id: o.user_id,
        name: u.name,
        mobile: u.mobile,
        title: o.title,
        logo: o.logo || null,
        product_type: o.product_type,
        status: o.flag_disbursed
          ? OFFER_LOAN_STATUS.DISBURSED
          : o.loan_sub_status || o.loan_status,
        at: o.clicked_at,
      };
    });

  return {
    kpis,
    ratios,
    funnel,
    product_split: productSplit,
    product_counts,
    trend: { categories, series },
    recent,
  };
}

function rateLabel(part, whole) {
  if (!whole) return null;
  return ((part / whole) * 100).toFixed(1) + "% of offers";
}

/* ----------------------------------------------------------------- leads */

/** Counts for each bucket tab, computed over the filtered (not paged) set. */
function leadBucketCounts(rows) {
  const counts = {};
  LEAD_BUCKETS.forEach((b) => (counts[b] = 0));
  rows.forEach((u) => u.buckets.forEach((b) => (counts[b] += 1)));
  return LEAD_BUCKETS.map((b) => ({
    label: b,
    count: counts[b],
    color: colorFor(b),
  }));
}

function filterLeads(params) {
  const [from, to] = params.date_range || [];
  return enrichedUsers.filter((u) => {
    if (!inRange(u.created, from, to)) return false;
    if (params.platform && u.platform !== params.platform) return false;
    if (!userInProductScope(u, params.product_type)) return false;
    if (!textMatch([u.name, u.mobile, u.email, u.customer_id, u.city], params.search_query))
      return false;
    return true;
  });
}

export function leads(params = {}) {
  const filtered = filterLeads(params);
  const counts = leadBucketCounts(filtered);

  // Counted at the same point as the bucket counts above -- every filter
  // applied except the one the control itself owns. So the number on a product
  // button is exactly what you get when you press it.
  //
  // userInProductScope is a `.some()`, so a user holding offers of both
  // products would count under both and the two buttons would sum to more than
  // the total. That cannot happen with the current data (a user's offers are
  // all one product), and the Leads header already warns its counts overlap.
  const acrossProducts = filterLeads({ ...params, product_type: null });
  const product_counts = PRODUCT_TYPES.map((label) => ({
    label,
    count: acrossProducts.filter((u) => userInProductScope(u, label)).length,
  }));

  let rows = filtered;
  if (params.bucket) rows = rows.filter((u) => u.buckets.indexOf(params.bucket) !== -1);

  rows = rows.slice().sort((a, b) => (a.last_activity < b.last_activity ? 1 : -1));

  return { ...paginate(rows, params), counts, product_counts };
}

export function leadDetail(id) {
  const user = enrichedUsers.find((u) => String(u.id) === String(id));
  if (!user) return null;

  const mine = offersFor(user.id).slice().sort((a, b) => b.priority - a.priority);
  const loan = loanFor(user.id);

  // The User -> Lead -> Offers -> Applications -> Status chain,
  // rendered as an ordered timeline on the detail screen.
  const timeline = [];
  timeline.push({
    key: "signup",
    label: "User signed up",
    at: user.created,
    detail: `${user.platform}${user.lead_partner ? " · " + user.lead_partner : ""}`,
    icon: "user",
    color: "#0288D1",
  });
  if (EC1_PROFILE_STATUSES.indexOf(user.profile_status) !== -1) {
    timeline.push({
      key: "ec1",
      label: "Sign up",
      at: null,
      detail: `Profile status: ${user.profile_status}`,
      icon: "clipboard-list",
      color: "#7B1FA2",
    });
  } else if (user.profile_status === PROFILE_STATUS.EC_REJECTED) {
    timeline.push({
      key: "ec_rejected",
      label: "Rejected at eligibility check",
      at: null,
      detail: user.rejection_reason || "Internal eligibility",
      icon: "circle-alert",
      color: "#C62828",
    });
  } else {
    timeline.push({
      key: "ec_done",
      label: "Eligibility completed",
      at: loan ? loan.created : null,
      detail: `Profile status: ${user.profile_status}`,
      icon: "circle-check",
      color: "#388E3C",
    });
  }
  if (mine.length) {
    timeline.push({
      key: "offers",
      label: `${mine.length} offer${mine.length > 1 ? "s" : ""} generated`,
      at: mine[0].created,
      detail: Array.from(new Set(mine.map((o) => o.product_type))).join(", "),
      icon: "tag",
      color: "#1976D2",
    });
  }
  mine
    .filter((o) => o.flag_offer_availed)
    .sort((a, b) => (a.clicked_at > b.clicked_at ? 1 : -1))
    .forEach((o) => {
      timeline.push({
        key: "click-" + o.id,
        label: `Clicked ${o.title}`,
        at: o.clicked_at,
        detail: "Lead created",
        icon: "mouse-pointer-click",
        color: "#00897B",
      });
    });
  mine
    .filter((o) => o.flag_disbursed)
    .forEach((o) => {
      timeline.push({
        key: "disb-" + o.id,
        label: `Disbursed · ${o.title}`,
        at: o.disbursal_date,
        detail: o.disbursal_amount ? "₹" + o.disbursal_amount.toLocaleString("en-IN") : "",
        icon: "circle-check",
        color: "#2E7D32",
      });
    });

  const notifications = notificationHistory
    .filter((n) => n.user_id === user.id)
    .slice(0, 12);

  return {
    user,
    loan,
    offers: mine,
    timeline,
    notifications,
    // Stated explicitly on the screen: attribution is first-touch on the user.
  };
}

/* ---------------------------------------------------------------- offers */

/**
 * Longest tenure in months mentioned in the string. Mirrors _tenure_months in
 * cashpo-server products/apis.py -- the column is free text on both sides.
 */
function tenureMonths(value) {
  if (!value) return null;
  const found = String(value).match(/\d+(?:\.\d+)?/g);
  if (!found) return null;
  let months = Math.max.apply(null, found.map(Number));
  if (String(value).toLowerCase().indexOf("year") !== -1) months *= 12;
  return months;
}

/** Rupees from a number or from prose ("Rs.20 Lakhs"). Mirrors _rupees. */
function rupees(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).toLowerCase();
  const found = text.replace(/,/g, "").match(/\d+(?:\.\d+)?/g);
  if (!found) return null;
  let amount = Number(
    text.indexOf("upto") !== -1 && found.length > 1 ? found[found.length - 1] : found[0]
  );
  if (text.indexOf("crore") !== -1) amount *= 10000000;
  else if (text.indexOf("lakh") !== -1 || text.indexOf("lac") !== -1) amount *= 100000;
  return amount;
}

/** An unparseable value never satisfies a bounded range. */
function inNumericRange(number, low, high) {
  const lo = low === undefined ? null : low;
  const hi = high === undefined ? null : high;
  if (lo === null && hi === null) return true;
  if (number === null || number === undefined) return false;
  if (lo !== null && number < lo) return false;
  if (hi !== null && number > hi) return false;
  return true;
}

/** Every product filter EXCEPT the state, which the stat strip owns. */
function matchesProductFilters(p, params) {
  if (!matchesProduct(p.product_type, params.product_type)) return false;
  if (!textMatch([p.name, p.product_type], params.search_query)) return false;
  if (!inNumericRange(tenureMonths(p.tenure), params.tenure_min, params.tenure_max)) {
    return false;
  }
  if (!inNumericRange(rupees(p.loan_upto), params.loan_upto_min, params.loan_upto_max)) {
    return false;
  }
  if (!inNumericRange(p.priority, params.priority_min, params.priority_max)) return false;
  if (params.flag_recomended !== undefined && params.flag_recomended !== null) {
    if (!!p.flag_recomended !== !!params.flag_recomended) return false;
  }
  return true;
}

export function offerCatalogue(params = {}) {
  let rows = products.filter((p) => {
    if (!matchesProductFilters(p, params)) return false;
    if (params.status === "active" && !p.is_active) return false;
    if (params.status === "inactive" && p.is_active) return false;
    return true;
  });

  // Attach live performance so the catalogue is decision-useful, not just config.
  rows = rows.map((p) => {
    const mine = offers.filter((o) => o.product_id === p.id);
    const clicked = mine.filter((o) => o.flag_offer_availed);
    return {
      ...p,
      offer_count: mine.length,
      click_count: clicked.length,
      disbursed_count: mine.filter((o) => o.flag_disbursed).length,
      rejected_count: mine.filter((o) => o.loan_status === OFFER_LOAN_STATUS.REJECTED).length,
      click_rate: mine.length ? clicked.length / mine.length : 0,
    };
  });

  rows.sort((a, b) => a.priority - b.priority || b.offer_count - a.offer_count);

  // The tiles carry every filter EXCEPT the state, because the state is what
  // they SET. Honouring it here would make them tautological -- pick "Disabled"
  // and Enabled would read 0, leaving no way back. Same rule the stage tabs on
  // the other screens follow.
  const scoped = products.filter((p) => matchesProductFilters(p, params));
  const summary = {
    total: scoped.length,
    active: scoped.filter((p) => p.is_active).length,
    inactive: scoped.filter((p) => !p.is_active).length,
  };

  // Totals per product, for the product switch at the top of the screen. Same
  // shape as the `counts` array Leads and Applications already return.
  const product_counts = PRODUCT_TYPES.map((label) => ({
    label,
    count: products.filter((p) => p.product_type === label).length,
  }));

  return {
    ...paginate(rows, params),
    summary,
    product_counts,
  };
}

export function offerDetail(id) {
  const product = products.find((p) => String(p.id) === String(id));
  if (!product) return null;

  const mine = offers.filter((o) => o.product_id === product.id);
  const clicked = mine.filter((o) => o.flag_offer_availed);
  const disbursed = mine.filter((o) => o.flag_disbursed);
  const rejected = mine.filter((o) => o.loan_status === OFFER_LOAN_STATUS.REJECTED);

  const eligibleUsers = mine.slice(0, 60).map((o) => {
    const u = enrichedUsers.find((x) => x.id === o.user_id) || {};
    return {
      offer_id: o.id,
      user_id: o.user_id,
      name: u.name,
      mobile: u.mobile,
      city: u.city,
      clicked: o.flag_offer_availed,
      clicked_at: o.clicked_at,
      status: o.flag_disbursed
        ? OFFER_LOAN_STATUS.DISBURSED
        : o.loan_sub_status || o.loan_status,
      disbursal_amount: o.disbursal_amount,
    };
  });

  return {
    product,
    stats: {
      eligible: mine.length,
      clicked: clicked.length,
      disbursed: disbursed.length,
      rejected: rejected.length,
      click_rate: mine.length ? clicked.length / mine.length : 0,
      disbursal_rate: clicked.length ? disbursed.length / clicked.length : 0,
      disbursed_amount: disbursed.reduce((s, o) => s + (o.disbursal_amount || 0), 0),
    },
    users: eligibleUsers,
  };
}

/** Enable / disable an offer. Mutates the in-memory dataset so the UI reflects it. */
export function setOfferActive(id, isActive) {
  const product = products.find((p) => String(p.id) === String(id));
  if (!product) return null;
  product.is_active = !!isActive;
  return product;
}

/** Create a product. Mirrors products/admin/product_create/. */
export function createOffer(payload) {
  const product = {
    id: Math.max.apply(null, products.map((p) => p.id)) + 1,
    name: payload.name,
    title: payload.name,
    product_type: payload.product_type,
    is_active: payload.is_active !== false,
    interest_rate: payload.interest_rate || "-",
    tenure: payload.tenure || "-",
    loan_upto: payload.loan_upto || "-",
    processing_fees: payload.processing_fees || "-",
    priority: Number(payload.priority) || 5,
    flag_recomended: !!payload.flag_recomended,
    enable_manual_application: payload.enable_manual_application !== false,
    web_url: payload.web_url || "",
    utm_link: payload.utm_link || "",
    color: DEFAULT_STATUS_COLOR,
    created: new Date().toISOString().slice(0, 10),
    eligibility: {
      minimum_age: Number(payload.minimum_age) || 21,
      maximum_age: Number(payload.maximum_age) || 58,
      minimum_income: Number(payload.minimum_income) || 0,
      maximum_income: null,
      employment_type: payload.employment_type || null,
      salary_type: null,
      cibil_score: Number(payload.cibil_score) || 0,
      pincode_count: 0,
    },
  };
  products.push(product);
  return product;
}

/** Update an existing offer's editable fields. */
export function updateOffer(id, payload) {
  const product = products.find((p) => String(p.id) === String(id));
  if (!product) return null;
  [
    "name", "interest_rate", "tenure", "loan_upto", "processing_fees",
    "web_url", "utm_link", "product_type",
  ].forEach((k) => {
    if (payload[k] !== undefined && payload[k] !== null) product[k] = payload[k];
  });
  if (payload.priority !== undefined) product.priority = Number(payload.priority);
  if (payload.flag_recomended !== undefined) product.flag_recomended = !!payload.flag_recomended;
  if (payload.is_active !== undefined) product.is_active = !!payload.is_active;
  product.title = product.name;
  return product;
}

/* ---------------------------------------------------------- applications */

/**
 * Which pipeline stage an offer sits in. Derived, because the backend has no
 * single "application stage" column -- it is spread across Offers.flag_disbursed,
 * Offers.loan_status, Offers.loan_sub_status and the user's profile_status.
 */
function stageFor(offer, user) {
  if (offer.flag_disbursed) return APPLICATION_STAGE.DISBURSED;
  if (offer.loan_status === OFFER_LOAN_STATUS.REJECTED) return APPLICATION_STAGE.REJECTED;
  if (offer.loan_sub_status === PRECHECK_STATUS.UNDER_PROCESS)
    return APPLICATION_STAGE.UNDER_PROCESS;
  if (user && EC1_PROFILE_STATUSES.indexOf(user.profile_status) !== -1)
    return APPLICATION_STAGE.EC1_PENDING;
  return APPLICATION_STAGE.OFFER_AVAILABLE;
}

/**
 * Every application row for ONE product, after date and search.
 *
 * Extracted so the product switch can count each product without changing what
 * the stage counts mean: those stay scoped to the product actually on screen,
 * while the buttons need a pass per product.
 */
function applicationRows(params, productType) {
  let rows = offers
    .filter((o) => matchesProduct(o.product_type, productType))
    .map((o) => {
      const u = userById(o.user_id) || {};
      const loan = loanFor(o.user_id);
      return {
        id: o.id,
        user_id: o.user_id,
        name: u.name,
        mobile: u.mobile,
        customer_id: u.customer_id,
        title: o.title,
        // products.Product.logo when the backend has one; null otherwise.
        logo: o.logo || null,
        product_type: o.product_type,
        stage: stageFor(o, u),
        precheck_status: loan ? loan.precheck_status : null,
        loan_status: o.loan_status,
        clicked: o.flag_offer_availed,
        clicked_at: o.clicked_at,
        created: o.created,
        disbursal_date: o.disbursal_date,
        disbursal_amount: o.disbursal_amount,
        reject_reason: o.remark,
        loan_amount: loan ? loan.loan_amount : null,
        // No `updated` column exists server-side, so "last modified" is the
        // newest thing that actually happened to this offer.
        last_modified: o.disbursal_date || o.clicked_at || o.created,
      };
    });

  // EC1 Pending is a USER-level state that happens BEFORE any offer exists, so
  // it can never appear on a per-offer row. Without these synthetic rows the
  // "EC1 Pending" stage would always read zero here while the Leads screen
  // reported dozens -- the two screens would contradict each other.
  const ec1Rows = enrichedUsers
    .filter((u) => EC1_PROFILE_STATUSES.indexOf(u.profile_status) !== -1)
    .filter((u) => userInProductScope(u, productType))
    .map((u) => ({
      id: "ec1-" + u.id,
      placeholder: true, // no offer exists yet; only stands in for the customer
      user_id: u.id,
      name: u.name,
      mobile: u.mobile,
      customer_id: u.customer_id,
      title: "—", // no offer yet
      logo: null,
      product_type: u.customer_type,
      stage: APPLICATION_STAGE.EC1_PENDING,
      precheck_status: null,
      loan_status: null,
      clicked: false,
      clicked_at: null,
      created: u.created,
      disbursal_date: null,
      disbursal_amount: null,
      reject_reason: null,
      loan_amount: null,
      last_modified: u.created,
    }));

  rows = rows.concat(ec1Rows);

  return rows;
}

/* ------------------------------------------- Offer Management predicates */

/** An empty selection means "no opinion", not "match nothing". */
function inList(selected, value) {
  if (!selected || !selected.length) return true;
  return selected.indexOf(value) !== -1;
}

/**
 * Facts about the PERSON. Applied to the customer, so they select who appears
 * without touching which of their offers are listed.
 */
function matchesUser(u, p) {
  if (!u) return false;
  if (!inRange(u.created, p.from_date, p.to_date)) return false;
  if (!inList(p.city, u.city)) return false;
  if (!inList(p.state, u.state)) return false;
  if (p.follow_up_date && u.follow_up_date !== p.follow_up_date) return false;
  if (p.is_assigned !== null && p.is_assigned !== undefined) {
    if (!!u.is_assigned !== !!p.is_assigned) return false;
  }
  if (p.agent_search_query && !textMatch([u.agent_name], p.agent_search_query)) {
    return false;
  }
  if (p.document_type && p.document_request_status && p.document_request_status.length) {
    if (p.document_request_status.indexOf(docStatusFor(u, p.document_type)) === -1) {
      return false;
    }
  }
  // A user with no application has no Perfios record; "Unassigned" covers it.
  if (!inList(p.perfios_status, u.perfios_status || "Unassigned")) return false;
  if (!inList(p.lead_partner, u.lead_source)) return false;
  return true;
}

/**
 * Facts about ONE offer. Used only with `.some()`: a customer qualifies if any
 * offer matches, and the row still carries all of them. Trimming the offers
 * instead would let a date filter rewrite a customer's stage -- exclude the
 * disbursed offer and "Disbursed" silently becomes "No offer clicked".
 */
function matchesOffer(r, p) {
  if (!inRange(r.created, p.loan_from_date, p.loan_to_date)) return false;
  if (p.disbursal_from_date && p.disbursal_to_date) {
    if (!r.disbursal_date) return false;
    if (!inRange(r.disbursal_date, p.disbursal_from_date, p.disbursal_to_date)) {
      return false;
    }
  }
  return true;
}

/** The single filtering pass. Tiles, product counts and the table all use it. */
function filterCustomers(customers, params) {
  return customers.filter((c) => {
    if (!matchesUser(userById(c.user_id), params)) return false;
    if (!c.offers.some((r) => matchesOffer(r, params))) return false;

    // Availed offers reads off the clicked count, so it agrees with the
    // "Clicked" column the operator is looking at.
    const min = params.availed_offer_count_min;
    const max = params.availed_offer_count_max;
    if (min !== null && min !== undefined && c.clicked_count < min) return false;
    if (max !== null && max !== undefined && c.clicked_count > max) return false;

    if (!textMatch([c.name, c.mobile, c.customer_id], params.search_query)) {
      return false;
    }
    return true;
  });
}

/**
 * The stage a CUSTOMER is at.
 *
 * Deliberately not "the furthest of their offer stages": between having offers
 * and being in process there are three distinct states that matter to the
 * business, and they are properties of the person's whole set rather than of
 * any one offer -- which is why "Offer Available" is not a customer stage at
 * all. Same vocabulary as the Leads buckets, so the two screens agree.
 *
 * Order matters: real progress outranks click activity, and Rejected is the
 * answer only when nothing else happened.
 */
function customerStage(rows) {
  const offers = rows.filter((r) => !r.placeholder);
  if (!offers.length) return APPLICATION_STAGE.EC1_PENDING;

  if (offers.some((r) => r.stage === APPLICATION_STAGE.DISBURSED))
    return APPLICATION_STAGE.DISBURSED;
  if (offers.some((r) => r.stage === APPLICATION_STAGE.UNDER_PROCESS))
    return APPLICATION_STAGE.UNDER_PROCESS;
  if (offers.every((r) => r.stage === APPLICATION_STAGE.REJECTED))
    return APPLICATION_STAGE.REJECTED;

  const clicked = offers.filter((r) => r.clicked).length;
  if (clicked === 0) return APPLICATION_STAGE.NO_OFFER_CLICKED;
  if (clicked === offers.length) return APPLICATION_STAGE.ALL_OFFERS_CLICKED;
  return APPLICATION_STAGE.SOME_OFFERS_CLICKED;
}

/**
 * One row per CUSTOMER, their matching offers nested underneath.
 *
 * Grouped here rather than in the component because pagination slices this
 * list: grouping after the slice would split a customer across pages.
 */
function groupByCustomer(rows) {
  const byUser = {};
  const order = [];

  rows.forEach((r) => {
    if (!byUser[r.user_id]) {
      byUser[r.user_id] = [];
      order.push(r.user_id);
    }
    byUser[r.user_id].push(r);
  });

  return order.map((user_id) => {
    let mine = byUser[user_id];
    const head = mine[0];

    // A user can be in an EC1 profile state AND hold offers, which produces a
    // synthetic "no offer yet" row alongside real ones. Flat that was
    // invisible; grouped it would sit next to three real offers and be false.
    const real = mine.filter((r) => !r.placeholder);
    if (real.length) mine = real;

    const disbursed = mine.reduce((sum, r) => sum + (r.disbursal_amount || 0), 0);
    const requested = mine.reduce(
      (max, r) => Math.max(max, r.loan_amount || 0),
      0
    );
    const rejected = mine.filter((r) => r.reject_reason);

    // Platform is a user-level fact, so it is read once here rather than being
    // hunted for on whichever offer row happens to be first.
    const u = userById(user_id) || {};

    return {
      id: user_id,
      user_id,
      name: head.name,
      mobile: head.mobile,
      customer_id: head.customer_id,
      platform: u.platform || null,
      offer_count: real.length,
      stage: customerStage(mine),
      // The customer-level answer for each column the table shows, so a
      // collapsed row is still a complete row rather than a mostly-empty one.
      precheck_status: mine.map((r) => r.precheck_status).filter(Boolean)[0] || null,
      clicked_count: mine.filter((r) => r.clicked).length,
      disbursal_amount: disbursed || null,
      loan_amount: requested || null,
      reject_reason: rejected.length ? rejected[0].reject_reason : null,
      // Applied is when they first appeared; modified is the newest thing that
      // happened to any of their offers.
      applied_at: mine.reduce(
        (min, r) => (r.created < min ? r.created : min),
        mine[0].created
      ),
      last_modified: mine.reduce(
        (max, r) => (r.last_modified > max ? r.last_modified : max),
        mine[0].last_modified
      ),
      offers: mine,
    };
  });
}

export function applications(params = {}) {
  // `date_range` is still accepted as an alias so other callers keep working,
  // but on this screen the range now means SIGNUP date for every row. It used
  // to be offer-created for real rows and user-created for the synthetic
  // no-offer ones -- two halves of one list measuring different things.
  const p = { ...params };
  if (!p.from_date && !p.to_date && Array.isArray(p.date_range)) {
    p.from_date = p.date_range[0];
    p.to_date = p.date_range[1];
  }

  const customers = filterCustomers(
    groupByCustomer(applicationRows(p, p.product_type)),
    p
  );

  // The tiles count CUSTOMERS, not offers. Three of the seven stages are facts
  // about a person's whole set of offers ("some clicked"), which cannot be
  // counted per offer at all -- and counting customers makes the tiles agree
  // with the table underneath instead of quietly measuring something else.
  // Every filter applies to the tiles EXCEPT the stage itself -- the same rule
  // the Leads buckets follow. Leaving the tiles unfiltered would show 40
  // Disbursed above a table of 3, and the screen promises the two agree.
  const counts = APPLICATION_STAGES.map((s) => ({
    label: s,
    count: customers.filter((c) => c.stage === s).length,
    color: colorFor(s),
  }));

  const product_counts = PRODUCT_TYPES.map((label) => ({
    label,
    count: filterCustomers(groupByCustomer(applicationRows(p, label)), p).length,
  }));

  // The stage is a property of the customer, so it selects WHO appears --
  // never which of their offers do. A customer shown under "Disbursed" still
  // lists every offer they hold, because that is the story of how they got
  // there.
  let scoped = customers;
  if (p.stage) scoped = scoped.filter((c) => c.stage === p.stage);
  scoped = scoped
    .slice()
    .sort((a, b) => (a.last_modified < b.last_modified ? 1 : -1));

  return { ...paginate(scoped, p), counts, product_counts };
}


/* ----------------------------------------------------------------- users */

/**
 * The ONE stage a user is at.
 *
 * Deliberately exclusive, unlike `bucketsFor` on the Leads side where a person
 * can be in several buckets at once and the tab counts therefore overlap and
 * exceed the total. Here the four counts add up to exactly the number of users.
 *
 * Order is the priority: the furthest thing that happened to them wins, and
 * "signed up but nothing yet" is the fallback.
 */
export function userStage(user) {
  const mine = offersFor(user.id);
  if (mine.some((o) => o.flag_disbursed)) return USER_STAGE.DISBURSED;
  if (user.profile_status === PROFILE_STATUS.EC_REJECTED) return USER_STAGE.REJECTED;
  if (mine.length && mine.every((o) => o.loan_status === OFFER_LOAN_STATUS.REJECTED)) {
    return USER_STAGE.REJECTED;
  }
  if (mine.length) return USER_STAGE.OFFERS_AVAILABLE;
  return USER_STAGE.SIGNUP;
}

/** Inclusive band match. A null bound is open-ended. */
function inBand(value, min, max) {
  if (min !== null && min !== undefined && value < min) return false;
  if (max !== null && max !== undefined && value > max) return false;
  return true;
}

/** Whole days between a timestamp and now, or null when there is no date. */
function daysAgo(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

function matchesBands(user, params) {
  if (params.age_min !== undefined || params.age_max !== undefined) {
    if (!inBand(user.age, params.age_min, params.age_max)) return false;
  }
  if (params.income_min !== undefined || params.income_max !== undefined) {
    if (!inBand(user.monthly_income, params.income_min, params.income_max)) return false;
  }
  if (params.active_days_min !== undefined || params.active_days_max !== undefined) {
    // Days ago, so the bounds invert: an upper bound on how long ago the user
    // was last active is a LOWER bound on the date itself.
    const days = daysAgo(user.last_activity);
    if (days === null) return false;
    if (!inBand(days, params.active_days_min, params.active_days_max)) return false;
  }
  if (params.cibil_no_score) {
    if (user.cibil_score) return false;
  } else if (params.cibil_min !== undefined || params.cibil_max !== undefined) {
    // A user with no score is not "below 650" -- they are a separate band.
    if (!user.cibil_score) return false;
    if (!inBand(user.cibil_score, params.cibil_min, params.cibil_max)) return false;
  }
  return true;
}

function filterUsers(params) {
  const [rangeFrom, rangeTo] = params.date_range || [];
  const from = params.from_date || rangeFrom;
  const to = params.to_date || rangeTo;
  return enrichedUsers.filter((u) => {
    if (!inRange(u.created, from, to)) return false;
    if (!userInProductScope(u, params.product_type)) return false;
    if (!inList(params.city, u.city)) return false;
    if (!inList(params.state, u.state)) return false;
    if (!inList(params.platform, u.platform)) return false;
    if (!inList(params.lead_partner, u.lead_source)) return false;
    if (!matchesBands(u, params)) return false;
    if (!textMatch([u.name, u.mobile, u.email, u.customer_id, u.city], params.search_query)) {
      return false;
    }
    return true;
  });
}

export function users(params = {}) {
  const filtered = filterUsers(params).map((u) => ({ ...u, stage: userStage(u) }));

  // Same rule as every other tabbed screen here: the tab counts carry every
  // filter EXCEPT the stage itself, so the number on a tab is exactly what you
  // get when you press it.
  const counts = USER_STAGES.map((s) => ({
    label: s,
    count: filtered.filter((u) => u.stage === s).length,
    color: colorFor(s),
  }));

  const acrossProducts = filterUsers({ ...params, product_type: null });
  const product_counts = PRODUCT_TYPES.map((label) => ({
    label,
    count: acrossProducts.filter((u) => userInProductScope(u, label)).length,
  }));

  let rows = filtered;
  if (params.stage) rows = rows.filter((u) => u.stage === params.stage);
  rows = rows.slice().sort((a, b) => (a.created < b.created ? 1 : -1));

  return { ...paginate(rows, params), counts, product_counts };
}

/* ----------------------------------------------------------------- utm */

// The UTM screen is gone; this lookup survives only because the Notifications
// composer still targets an audience by acquisition source.
export function utmDropdown() {
  return utmOptions;
}

/** Option lists for the Offer Management filter panel. */
export function filterDropdowns() {
  return filterOptions;
}

/* --------------------------------------------------------- notifications */

export function notificationAudience(params = {}) {
  // Estimated reach for the chosen segment, computed from the same buckets the
  // Leads screen uses so the two screens can never disagree.
  let rows = enrichedUsers;
  if (params.bucket) rows = rows.filter((u) => u.buckets.indexOf(params.bucket) !== -1);
  if (params.utm_source) rows = rows.filter((u) => u.utm_source === params.utm_source);
  rows = rows.filter((u) => userInProductScope(u, params.product_type));

  const withEmail = rows.filter((u) => !!u.email).length;
  const withMobile = rows.length;

  return {
    total: rows.length,
    reachable: {
      [CHANNEL.PUSH]: rows.filter((u) => u.platform === "Android").length,
      [CHANNEL.WHATSAPP]: withMobile,
      [CHANNEL.SMS]: withMobile,
      [CHANNEL.EMAIL]: withEmail,
    },
    sample: rows.slice(0, 8).map((u) => ({
      id: u.id,
      name: u.name,
      mobile: u.mobile,
      email: u.email,
      platform: u.platform,
      buckets: u.buckets,
    })),
  };
}

export function notifications(params = {}) {
  let rows = notificationHistory.slice();
  // params.channel is an array when an admin locked it for this user.
  if (params.channel)
    rows = rows.filter((n) => matchesAny(n.channel, params.channel));
  if (params.status) rows = rows.filter((n) => n.status === params.status);
  rows = rows.filter((n) => textMatch([n.name, n.mobile, n.title, n.template], params.search_query));

  const byChannel = {};
  notificationHistory.forEach((n) => {
    byChannel[n.channel] = (byChannel[n.channel] || 0) + 1;
  });

  const byStatus = {};
  notificationHistory.forEach((n) => {
    byStatus[n.status] = (byStatus[n.status] || 0) + 1;
  });

  const delivered = notificationHistory.filter(
    (n) =>
      n.status === DELIVERY_STATUS.DELIVERED ||
      n.status === DELIVERY_STATUS.READ ||
      n.status === DELIVERY_STATUS.LINK_CLICKED
  ).length;

  return {
    ...paginate(rows, params),
    summary: {
      total: notificationHistory.length,
      delivered,
      delivery_rate: notificationHistory.length ? delivered / notificationHistory.length : 0,
      failed: notificationHistory.filter((n) => n.status === DELIVERY_STATUS.FAILED).length,
      by_channel: Object.keys(byChannel).map((k) => ({ label: k, value: byChannel[k] })),
      by_status: Object.keys(byStatus).map((k) => ({
        label: k,
        value: byStatus[k],
        color: colorFor(k),
      })),
    },
  };
}

/* ------------------------------------------------ notification history */

/**
 * The Notification History's shape, for a build with the module switched back
 * to mock. Deterministic, so paging and the filters can be exercised.
 */
export function automationHistory(params = {}) {
  const outcomes = [
    ["dry_run", "would_send", "Would send (dry run)"],
    ["dry_run", "skipped_daily_cap", "Skipped (dry run)"],
    ["live", "provider_accepted", "Provider accepted"],
    ["live", "provider_failed", "Failed"],
  ];
  const channels = ["Push", "Whatsapp", "SMS", "Email"];
  const groups = {
    would_send: "would_send",
    skipped_daily_cap: "skipped",
    provider_accepted: "provider_accepted",
    provider_failed: "failed",
  };
  const base = Date.parse("2026-10-07T09:00:00+05:30");
  let rows = Array.from({ length: 37 }, (_, i) => {
    const [source, outcome, label] = outcomes[i % outcomes.length];
    const legacy = i % 5 === 4;
    const counts = {
      loan_applications: i % 3,
      offers_generated: (i * 7) % 17,
      offers_clicked: i % 4,
      disbursed_loans: i % 6 === 0 ? 1 : 0,
    };
    const summary = !counts.loan_applications && !counts.offers_generated
      ? "No loan applications or offers"
      : `${counts.offers_generated} offers generated; ${counts.offers_clicked} clicked; ${counts.disbursed_loans} disbursed loans`;
    return {
      log_id: `${source === "live" ? "S" : "D"}-${1000 - i}`,
      id: 1000 - i,
      source,
      created: new Date(base - i * 3600 * 1000).toISOString(),
      user_id: 500 + (i % 9),
      customer_name: i % 7 === 3 ? "" : `Customer ${500 + (i % 9)}`,
      mobile: `98765${String(43210 + (i % 9)).padStart(5, "0")}`,
      contact: `98765${String(43210 + (i % 9)).padStart(5, "0")}`,
      channel: channels[i % channels.length],
      title: i % 6 === 5 ? "Untitled" : "Your offer is waiting",
      subject: i % 6 === 5 ? "" : "Your offer is waiting",
      template_name: "offer_nudge_v2",
      step_key: i % 8 === 7 ? "signup:0" : "agent:offer_nudge",
      reference: "automation:offer_not_clicked:day-2",
      body: "Hi {name}, your pre-approved offer is still available.".replace("{name}", `Customer ${500 + (i % 9)}`),
      outcome,
      outcome_group: groups[outcome],
      outcome_label: label,
      outcome_detail: outcome === "skipped_daily_cap" ? "daily_cap" : "",
      customer_status: {
        source: legacy ? "current" : "snapshot",
        as_of: new Date(base - i * 3600 * 1000).toISOString(),
        counts,
        summary,
      },
    };
  });
  rows.forEach((r) => {
    r.source_label = r.source === "dry_run" ? "Dry run" : "Real send";
    r.sender_label = /^agent:/.test(r.step_key) ? "Growth agent" : "Welcome message";
  });
  if (String(params.received) === "1") {
    // The User history: only what went out, plus messages sent outside the
    // engine (ReceivedMessageLog, "R-" ids).
    rows = rows.filter((r) => r.outcome === "would_send" || r.outcome === "provider_accepted");
    rows = rows.concat(
      Array.from({ length: 6 }, (_, i) => ({
        log_id: `R-${200 - i}`,
        id: 200 - i,
        source: "received",
        source_label: "Real send",
        created: new Date(base - (i * 5 + 2) * 3600 * 1000).toISOString(),
        user_id: 500 + (i % 9),
        customer_name: `Customer ${500 + (i % 9)}`,
        mobile: `98765${String(43210 + (i % 9)).padStart(5, "0")}`,
        contact: `98765${String(43210 + (i % 9)).padStart(5, "0")}`,
        channel: i % 2 ? "Push" : "SMS",
        title: i % 2 ? "EMI Reminder" : "EMI reminder SMS",
        subject: i % 2 ? "EMI Reminder" : "",
        template_name: "",
        step_key: "",
        reference: "",
        origin: "emi_reminder",
        sender_label: "EMI reminder",
        body: "Your EMI of Rs 2,450 is due on 20 Oct.",
        outcome: "provider_accepted",
        outcome_group: "provider_accepted",
        outcome_label: "Provider accepted",
        outcome_detail: "",
      }))
    );
    rows.sort((a, b) => Date.parse(b.created) - Date.parse(a.created));
  }
  if (params.user_id) rows = rows.filter((r) => String(r.user_id) === String(params.user_id));
  if (String(params.static) === "0") {
    rows = rows.filter((r) => /^agent:/.test(r.step_key));
  }
  if (params.channel) rows = rows.filter((r) => r.channel === params.channel);
  if (params.title && params.title.length) {
    rows = rows.filter((r) => params.title.indexOf(r.title) !== -1);
  }
  if (params.outcome) rows = rows.filter((r) => r.outcome_group === params.outcome);
  if (params.search) {
    const q = String(params.search).toLowerCase();
    rows = rows.filter(
      (r) =>
        String(r.user_id) === q ||
        r.mobile.indexOf(q) !== -1 ||
        r.customer_name.toLowerCase().indexOf(q) !== -1
    );
  }
  const length = Number(params.page_length) || 20;
  const pages = Math.max(1, Math.ceil(rows.length / length));
  const page = Math.min(Math.max(1, Number(params.page_number) || 1), pages);
  return {
    results: rows.slice((page - 1) * length, page * length),
    total_count: rows.length,
    total_page_count: pages,
    page_number: page,
  };
}

/** The User history: one row per borrower, grouped from the received rows. */
export function automationHistoryUsers(params = {}) {
  const rows = automationHistory({
    received: 1,
    page_length: 1000,
    static: params.static,
    channel: params.channel,
    search: params.search,
  }).results;
  const byUser = {};
  rows.forEach((r) => {
    const u = byUser[r.user_id] || (byUser[r.user_id] = {
      user_id: r.user_id,
      customer_name: r.customer_name,
      mobile: r.mobile,
      received_count: 0,
      would_send_count: 0,
      last_received_at: r.created,
      last_title: r.title,
      last_body: r.body,
      last_subject: r.subject,
      last_channel: r.channel,
      channels: {},
    });
    u.received_count += 1;
    if (r.source === "dry_run") u.would_send_count += 1;
    u.channels[r.channel] = (u.channels[r.channel] || 0) + 1;
  });
  const users = Object.values(byUser)
    .map((u) => ({
      ...u,
      channels: Object.keys(u.channels)
        .map((c) => ({ channel: c, count: u.channels[c] }))
        .sort((a, b) => b.count - a.count),
    }))
    .sort((a, b) => Date.parse(b.last_received_at) - Date.parse(a.last_received_at));
  const length = Number(params.page_length) || 20;
  const pages = Math.max(1, Math.ceil(users.length / length));
  const page = Math.min(Math.max(1, Number(params.page_number) || 1), pages);
  return {
    results: users.slice((page - 1) * length, page * length),
    total_count: users.length,
    total_page_count: pages,
    page_number: page,
    count_is_capped: false,
  };
}

export function automationHistoryTitles(params = {}) {
  const titles = new Set(
    automationHistory({ page_length: 1000, static: params.static }).results.map((r) => r.title)
  );
  return Array.from(titles).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}

export default {
  dashboard,
  leads,
  leadDetail,
  offerCatalogue,
  offerDetail,
  setOfferActive,
  createOffer,
  updateOffer,
  applications,
  users,
  userStage,
  utmDropdown,
  filterDropdowns,
  notifications,
  notificationAudience,
  automationHistory,
  automationHistoryUsers,
  automationHistoryTitles,
};
